import { JEFE_4 } from '../../data/boss-objects';
import { SIM_HZ } from '../../data/tuning';

/**
 * Where El Chato is (JEFE-4, GDD §9.2, issue #133). He never flies: the tunnels hold him. The cycle is
 *
 *   HIDDEN > WARNING (dust, 1.0 s) > OUT (2.5 s, throwing barrels) | MOTO (crossing) > HIDING > HIDDEN
 *
 * Pure and deterministic: every duration is counted in ticks and the tunnel of an exit comes from the seeded PRNG of the fight
 * (the first one) and then alternates. Decoys only hurt him while he is OUT or on the MOTO; touching his body hurts only then.
 */
export type ChatoPhase = 'HIDDEN' | 'WARNING' | 'OUT' | 'HIDING' | 'MOTO';
export type ChatoTunnel = 'floor' | 'ceiling' | 'horizontal';

export interface ChatoState {
  state: ChatoPhase;
  /** Ticks in the current state. */
  ticks: number;
  tunnel: ChatoTunnel;
  /** Exits so far (the first tunnel is chosen by the PRNG, the next ones alternate). */
  exits: number;
  /** Barrels thrown in the current exit. */
  barrels: number;
}

export const HIDDEN_TICKS = Math.round(JEFE_4.hiddenSeconds * SIM_HZ);
export const WARNING_TICKS = Math.round(JEFE_4.warningSeconds * SIM_HZ);
export const OUT_TICKS = Math.round(JEFE_4.outSeconds * SIM_HZ);
export const HIDING_TICKS = Math.round(JEFE_4.hidingSeconds * SIM_HZ);
/** Ticks into an exit at which each barrel leaves. */
export const BARREL_TICKS: readonly number[] = JEFE_4.barrelAtSeconds.map((seconds) => Math.round(seconds * SIM_HZ));

export const createChato = (): ChatoState => ({ state: 'HIDDEN', ticks: 0, tunnel: 'floor', exits: 0, barrels: 0 });

/** Whether a decoy hurts him and whether his body is harmful: only while he is out of his tunnel or on the motorbike. */
export const isChatoVulnerable = (chato: ChatoState | null): boolean => chato !== null && (chato.state === 'OUT' || chato.state === 'MOTO');

/** The tunnel of the next exit: the first one from the PRNG, the next ones alternate floor and ceiling. */
export function nextTunnel(chato: ChatoState, rng: () => number): ChatoTunnel {
  if (chato.exits === 0) return rng() < 0.5 ? 'floor' : 'ceiling';
  return chato.tunnel === 'floor' ? 'ceiling' : 'floor';
}

/** Height of the centre of his body at the mouth of a tunnel. */
export const mouthY = (tunnel: ChatoTunnel): number => (tunnel === 'ceiling' ? JEFE_4.ceilingY : tunnel === 'horizontal' ? JEFE_4.motoY : JEFE_4.floorY);

/** The ticks a motorbike takes to cross a view of `viewWidth` u (plus a margin on each side) at the scroll speed plus 4 u/s. */
export const crossingTicks = (scrollSpeed: number, viewWidth: number, margin = 2): number =>
  Math.ceil(((viewWidth + 2 * margin) / (scrollSpeed + JEFE_4.motoExtraSpeed)) * SIM_HZ);
